// @flow
import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import Group from 'sail/Group';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import reduce from 'lodash/reduce';
import mapValues from 'lodash/mapValues';
import PropertyList, {PropertyListItem} from 'sail/PropertyList';
import {CaptionMono} from 'sail/Text';

import {useAccount} from 'docs/data';
import {makeStripeRequest} from './resource';
import {Spinner} from '../cli/general';
import CollapsibleJson from '../../components/CollapsibleJson/CollapsibleJson';
import ButtonLink from '../../components/ButtonLink';

import type {
  FixtureValue,
  FixtureParams,
  Fixture,
  FixtureConfig,
} from './triggers';

const interpolationPattern = /\$\{([^${}]+)\}/g;
const interpolationKeyPattern = /(?<name>.+):(?<path>.+)/g;

type PastResults = {
  [string]: {},
};

export const transformFixtureValue = ({
  fixtureValue,
  pastResults,
}: {
  pastResults: PastResults,
  fixtureValue: FixtureValue,
}): FixtureValue => {
  if (typeof fixtureValue !== 'string') {
    return fixtureValue;
  }
  if (!interpolationPattern.test(fixtureValue)) {
    return fixtureValue;
  }

  return fixtureValue.replace(interpolationPattern, (_, interpolation) => {
    return interpolation.replace(
      interpolationKeyPattern,
      (match, name, path) => {
        return get(pastResults[name], path);
      },
    );
  });
};

export const transformFixtureParams = ({
  fixtureParams,
  pastResults,
}: {
  fixtureParams: FixtureParams,
  pastResults: PastResults,
}): {[string]: FixtureParams} =>
  reduce(
    fixtureParams,
    (transformed, value, key: string) => {
      if (Array.isArray(value)) {
        transformed[key] = value.map((subParam) => {
          if (isObject(subParam)) {
            return transformFixtureParams({
              fixtureParams: subParam,
              pastResults,
            });
          } else {
            return transformFixtureValue({fixtureValue: subParam, pastResults});
          }
        });
      } else if (isObject(value)) {
        transformed[key] = mapValues(value, (subParams) => {
          if (isObject(subParams)) {
            return transformFixtureParams({
              fixtureParams: subParams,
              pastResults,
            });
          } else {
            return transformFixtureValue({
              fixtureValue: subParams,
              pastResults,
            });
          }
        });
      } else {
        transformed[key] = transformFixtureValue({
          fixtureValue: value,
          pastResults,
        });
      }

      return transformed;
    },
    {},
  );

export const transformFixture = ({
  fixture,
  pastResults,
}: {
  fixture: Fixture,
  pastResults: PastResults,
}): Fixture => ({
  name: String(
    transformFixtureValue({fixtureValue: fixture.name, pastResults}),
  ),
  path: String(
    transformFixtureValue({fixtureValue: fixture.path, pastResults}),
  ),
  method: fixture.method,
  ...(fixture.params
    ? {
        params: transformFixtureParams({
          fixtureParams: fixture?.params ?? {},
          pastResults,
        }),
      }
    : {}),
});

export const useTriggers = ({
  fixtures,
  api_key,
}: {
  fixtures: Array<Fixture>,
  api_key: string,
}) => {
  const {account} = useAccount();
  const requests = React.useRef({});
  const responses = React.useRef({});
  const [fixtureSetup, setFixtureSetup] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function executeFixtures() {
      setIsLoading(true);

      for (const rawFixture of fixtures) {
        setFixtureSetup((prevState) => [...prevState, rawFixture.name]);

        const fixture = transformFixture({
          fixture: rawFixture,
          pastResults: responses.current,
        });

        // Normally this linting rule makes sense because you want to
        // perform requests in parallel and use `Promise.all()`, but in
        // this case each request is dependent on the previous ones so
        // we need to perform them sequentially. Details found here:
        // https://eslint.org/docs/rules/no-await-in-loop
        //
        // eslint-disable-next-line no-await-in-loop
        const response = await makeStripeRequest({
          url: `${account.hosts['api.stripe.com']}${fixture.path}`,
          method: fixture.method,
          api_key,
          raw_params: fixture.params ?? {},
          additional_headers: {},
        });

        responses.current = {...responses.current, [fixture.name]: response};
        requests.current = {
          ...requests.current,
          [fixture.name]: {
            url: `${account.hosts['api.stripe.com']}${fixture.path}`,
            method: fixture.method,
            parameters: fixture.params ?? {},
          },
        };

        if (response?.error?.message) {
          setError(response?.error?.message);
          setIsLoading(false);
          break;
        }
      }

      setIsLoading(false);
    }

    executeFixtures();
  }, [account.hosts, api_key, fixtures]);

  return {
    isLoading,
    responses: responses.current,
    requests: requests.current,
    error,
    fixtureSetup,
  };
};

export const messages = defineMessages({
  settingUpFixture: {
    id: 'docsApp.cluiTrigger.settingUpFixture',
    description: 'Message shown when the Stripe fixture is being applied.',
    defaultMessage: 'Setting up fixture for: {name}',
  },
  setUpFixture: {
    id: 'docsApp.cluiTrigger.setUpFixture',
    description: 'Message shown when the Stripe fixture has been applied.',
    defaultMessage: 'Set up fixture for: {name}',
  },
  triggerFailed: {
    id: 'docsApp.cluiTrigger.failed',
    description: 'Message shown when the Stripe fixture request has failed.',
    defaultMessage: 'Trigger failed: {error}',
  },
  request: {
    id: 'docsApp.cluiTrigger.request',
    description: 'Label for the request property list key.',
    defaultMessage: 'Request',
  },
  response: {
    id: 'docsApp.cluiTrigger.response',
    description: 'Label for the response property list key.',
    defaultMessage: 'Response',
  },
  triggerSucceeded: {
    id: 'docsApp.cluiTrigger.succeeded',
    description:
      'Message shown when all the Stripe fixture requests have succeeded.',
    defaultMessage: 'Trigger succeeded!',
  },
  showDetails: {
    id: 'docsApp.cluiTrigger.showDetails',
    description:
      'Label for the action to show details of each request made for the trigger command.',
    defaultMessage: 'Show details…',
  },
  hideDetails: {
    id: 'docsApp.cluiTrigger.hideDetails',
    description:
      'Label for the action to hide details of each request made for the trigger command.',
    defaultMessage: 'Hide details…',
  },
});

export const TriggerResponse = ({
  fixtureConfig,
  api_key,
  setAllowInput,
}: {
  fixtureConfig: FixtureConfig,
  api_key: string,
  setAllowInput: (boolean) => void,
}) => {
  const {formatMessage} = useIntl();
  const [showDetails, setShowDetails] = React.useState(false);
  const {isLoading, error, fixtureSetup, responses, requests} = useTriggers({
    fixtures: fixtureConfig.fixtures,
    api_key,
  });

  React.useEffect(() => {
    if (!isLoading || error) {
      setAllowInput(true);
    }
  }, [isLoading, error, setAllowInput]);

  const fixtureList = (
    <Group
      direction="vertical"
      alignItems="flex-start"
      justifyContent="flex-start"
    >
      {fixtureSetup.map((name, idx) =>
        idx === fixtureSetup.length - 1 && isLoading && !error ? (
          <Spinner
            text={formatMessage(messages.settingUpFixture, {name})}
            key={idx}
          />
        ) : (
          <span key={idx}>{formatMessage(messages.setUpFixture, {name})}</span>
        ),
      )}
    </Group>
  );

  if (isLoading) {
    return fixtureList;
  } else {
    return (
      <Group
        direction="vertical"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        {showDetails ? (
          <Group
            direction="vertical"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            {fixtureSetup.map((fixtureName, idx) => (
              <React.Fragment key={fixtureName}>
                <pre>
                  {formatMessage(messages.setUpFixture, {name: fixtureName})}
                </pre>
                <PropertyList
                  layout="stacked"
                  outerPadding={0}
                  className="TriggerDetails"
                >
                  <PropertyListItem
                    label={
                      <CaptionMono color="gray300">
                        {formatMessage(messages.request)}
                      </CaptionMono>
                    }
                    definition={
                      <CollapsibleJson data={requests[fixtureName] ?? {}} />
                    }
                  />
                </PropertyList>
                <PropertyList
                  layout="stacked"
                  outerPadding={0}
                  className="TriggerDetails"
                >
                  <PropertyListItem
                    label={
                      <CaptionMono color="gray300">
                        {formatMessage(messages.response)}
                      </CaptionMono>
                    }
                    definition={
                      <CollapsibleJson data={responses[fixtureName] ?? {}} />
                    }
                  />
                </PropertyList>
              </React.Fragment>
            ))}
          </Group>
        ) : (
          fixtureList
        )}
        <Group
          alignItems="baseline"
          justifyContent="flex-start"
          direction="horizontal"
          spacing={8}
        >
          <span>
            {error
              ? formatMessage(messages.triggerFailed, {error})
              : formatMessage(messages.triggerSucceeded)}
          </span>
          <ButtonLink
            onClick={() => {
              setShowDetails((prevState) => !prevState);
            }}
            label={formatMessage(
              showDetails ? messages.hideDetails : messages.showDetails,
            )}
          />
        </Group>
      </Group>
    );
  }
};
